import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { listAssets, initializeStorage } from '../../lib/assetsApi';

const { FiFolder, FiRefreshCw, FiCheck, FiX, FiSettings } = FiIcons;

const DropboxTester = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [error, setError] = useState(null);
  const [initResult, setInitResult] = useState(null);

  const handleListAssets = async (folder = '') => {
    setLoading(true);
    setError(null);

    try {
      const result = await listAssets(folder);
      setAssets(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async () => {
    setInitializing(true);
    setError(null);
    setInitResult(null);

    try {
      const result = await initializeStorage();
      setInitResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setInitializing(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="max-w-6xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Dropbox Tester</h1>
          <p className="text-gray-600">Test Dropbox storage integration</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Controls */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Controls</h2>
            
            <div className="space-y-4">
              <button
                onClick={handleInitialize}
                disabled={initializing}
                className="w-full flex items-center justify-center space-x-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                <SafeIcon 
                  icon={initializing ? FiRefreshCw : FiSettings} 
                  className={`w-4 h-4 ${initializing ? 'animate-spin' : ''}`} 
                />
                <span>{initializing ? 'Initializing...' : 'Initialize Storage'}</span>
              </button>

              <button
                onClick={() => handleListAssets()}
                disabled={loading}
                className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                <SafeIcon 
                  icon={loading ? FiRefreshCw : FiFolder} 
                  className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} 
                />
                <span>{loading ? 'Loading...' : 'List Root Assets'}</span>
              </button>

              <div className="grid grid-cols-2 gap-2">
                {['logos', 'watermarks', 'documents', 'processed'].map(folder => (
                  <button
                    key={folder}
                    onClick={() => handleListAssets(folder)}
                    disabled={loading}
                    className="flex items-center justify-center space-x-1 bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    <SafeIcon icon={FiFolder} className="w-3 h-3" />
                    <span>{folder}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Initialize Result */}
            {initResult && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-2 mb-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                  <h3 className="font-medium text-green-800">Storage Initialized</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <p className="text-green-700">
                    <span className="font-medium">Root Path:</span> {initResult.data?.rootPath}
                  </p>
                  <div>
                    <span className="font-medium text-green-700">Folders:</span>
                    <ul className="mt-1 space-y-1">
                      {initResult.data?.folders?.map((folder, index) => (
                        <li key={index} className="text-green-600 text-xs">
                          {folder.folder}: {folder.status}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <SafeIcon icon={FiX} className="w-5 h-5 text-red-600" />
                  <h3 className="font-medium text-red-800">Error</h3>
                </div>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
          </div>

          {/* Assets List */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Assets ({assets.length})
            </h2>
            
            {assets.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {assets.map((asset, index) => (
                  <div key={index} className="p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{asset.name}</p>
                        <p className="text-sm text-gray-600 font-mono truncate">{asset.path}</p>
                        <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                          <span>{formatSize(asset.size)}</span>
                          <span>{formatDate(asset.modified)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <SafeIcon icon={FiFolder} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No assets found</p>
                <p className="text-sm text-gray-500">Click "List Assets" to load files</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DropboxTester;